//#region imports
import React, { useEffect, useState } from 'react';

// tremor
import { BarChart } from "@tremor/react";

// custom components
import { Title, NewScreen } from "_atoms";


// styling
import { Colors, Spacing } from '_styles';
import styled from 'styled-components';


// bootstrap
import { Row, Col } from 'react-bootstrap';


// custom components
import TotalReportsFilters from './total-reports-filters';
import FlightsConvReports from './flights-conv-reports';
import CustomReportsTable from './custom-reports-table';
import TimelineAnalyticsTable from '../timeline/timeline-components/timeline-analytics-table';
import SLAReportsCharts from './sla-reports-charts';
import FlightReport from './flight-report';
import MovementReportPage from './movement-report-page';
import SunflowerReportSummary from './sunflower-report-summary';

//redux
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedReport, reset, selectSLAChartData, selectAirlinePerformanceChartData, selectFlightsListReport, selectReport, selectSunflowerReport } from '_redux/slices/total-reports/total-reports';
import { getAirportLogo, getAirportBase64Logo } from '_redux/slices/analytics/reviews';
import { selectUserDb } from '_redux/slices/main-screen/user';
import { resetGetPreAdvisedAndWalkups } from '_redux/slices/timeline/timeline-data';
import { getEmployeeList } from '_redux/slices/timeline/employees';
import { ReportsTypes } from '_utils/total-reports-constants';
//#endregion

const StyledParagraph = styled.p`
    cursor: pointer;
    color: ${props => (props.color)};
    background-color: ${props => (props.backgroundColor)};
    font-weight: 400;
    width: 100%;
    font-size: 15px;
    padding: 8px 8px 8px 8px;
`;

const TotalReports = () => {

    //#region redux
    const dispatch = useDispatch();

    const selectedReport = useSelector(selectSelectedReport);                            // holds the value of the selected reports to export and preview
    const SLAChartData = useSelector(selectSLAChartData);                                // holds the value SLA report chart data
    const AirlinePerformanceChartData = useSelector(selectAirlinePerformanceChartData);
    const flightsListReport = useSelector(selectFlightsListReport);
    const sunflowerReport = useSelector(selectSunflowerReport);                          // sunflower lanyard report payload (summary + byAirport + transactions)
    const dbUser = useSelector(selectUserDb);
    //#endregion

    //#region local states
    const [excelRows, setExcelRows] = useState(null);                                    // viewed report rows
    const [excelColumns, setExcelColumns] = useState(null);                              // viewed report columns
    const [hover, setHover] = useState(null);
    //#endregion

    //#region useEffect
    //reset all 
    useEffect(() => {
        dispatch(reset());
        dispatch(resetGetPreAdvisedAndWalkups());
    }, []);

    // get logos
    useEffect(() => {
        if (dbUser) {
            dispatch(getAirportLogo(dbUser.details.authority));
            dispatch(getAirportBase64Logo(dbUser.details.authority));
            dispatch(getEmployeeList(dbUser.details.authority));
        }
    }, [])

    const handleSelectReport = (type) => {
        dispatch(selectReport(type));
        dispatch(resetGetPreAdvisedAndWalkups());
    }
    //#endregion

    const NoReportSelected = () => {
        return (
            <>
                {!selectedReport && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "69vh",
                            // border: "2px dashed #cacaca",
                            // borderRadius: "12px",
                            background: "#fafafa",
                            color: Colors.GRAY_DARK,
                            padding: "30px",
                            textAlign: "center",
                            gap: "15px",
                            marginTop: "30px"
                        }}
                    >

                        <p style={{ maxWidth: "400px", margin: 0 }}>
                            Please select a report type to generate your data summary.
                            You can filter specific fields, to analyze your passengers traffic, and export the file as Excel.
                        </p>

                    </div>
                )}
            </>
        )
    }

    // to return, done here as it's easier when adding sidebar or not
    const content = (
        <>

            {/* reports filters */}
            <Row style={{ overscrollBehaviorY: "contain", opacity: 1 }}>
                {/* Reports Types List */}
                <Col xs={12} md={2} lg={2} style={{ backgroundColor: "#ffffff", minHeight: "100vh", overflowY: 'auto' }} >
                    {/* <p>Select Report:</p> */}
                    {ReportsTypes.filter(report => {
                        const isAllAirline = dbUser.details.type === "AIRLINE" && dbUser.details.authority === "ALL";
                        if (isAllAirline) {
                            return report.value === "arrivalBookings" || report.value === "departuresBookings";
                        }
                        return report.airports.includes(dbUser.details.authority) || report.airports[0] === "A";
                    }).map((type, index) =>
                        <StyledParagraph
                            key={index}
                            onMouseOver={() => setHover(type.value)}
                            onMouseLeave={() => { setHover(null) }}
                            color={hover == type.value || selectedReport == type.value ? '#fff' : Colors.SIDEBAR_COLOR}
                            backgroundColor={hover == type.value || selectedReport == type.value ? Colors.SIDEBAR_COLOR : '#fff'}
                            onClick={() => handleSelectReport(type.value)}
                        >
                            {type.label}
                        </StyledParagraph>
                    )}
                </Col>
                {/* report content */}
                <Col xs={12} md={10}>

                    <TotalReportsFilters setExcelRows={setExcelRows} setExcelColumns={setExcelColumns} />

                    {/* Sunflower Lanyard - Paid vs Collection summary panel */}
                    {selectedReport === "sunflowerLanyard" && excelColumns && sunflowerReport?.summary && (
                        <SunflowerReportSummary summary={sunflowerReport.summary} byAirport={sunflowerReport.byAirport} />
                    )}

                    {/* report table */}
                    {excelColumns && !["flightsConversations"].includes(selectedReport) && (
                        <div style={{ height: "69vh", overflow: "scroll", marginTop: "20px" }}>
                            <CustomReportsTable data={excelRows} columns={excelColumns} maxHeight={"unset"} minHeight={"unset"} />
                        </div>
                    )}

                    {!selectedReport && <NoReportSelected />}
                    {/* SLA Report Charts */}
                    {selectedReport === "SLAReport" && SLAChartData && <SLAReportsCharts data={SLAChartData} />}

                    {/* Airline Performance Charts */}
                    {selectedReport === "AirlinePerformanceReport" && AirlinePerformanceChartData && (
                        <>
                            {AirlinePerformanceChartData.AirlinePerformanceInbound && (
                                <BarChart
                                    data={AirlinePerformanceChartData.AirlinePerformanceInbound}
                                    dataKey="name"
                                    categories={["Total"]}
                                    showLegend={false}
                                    showYAxis={false}
                                    startEndOnly={true}
                                    className="-mb-2 mt-8 h-48"
                                />
                            )}
                            {AirlinePerformanceChartData.AirlinePerformanceOutbound && (
                                <BarChart
                                    data={AirlinePerformanceChartData.AirlinePerformanceOutbound}
                                    dataKey="name"
                                    categories={["Total"]}
                                    showLegend={false}
                                    showYAxis={false}
                                    startEndOnly={true}
                                    className="-mb-2 mt-8 h-48"
                                />
                            )}
                        </>
                    )}

                    {/* Pre-Advised, Advanced And Walk up */}
                    {selectedReport === "preAdvisedAndAdvanced" && <TimelineAnalyticsTable toTotalReports={true} />}

                    {/* Flights List */}
                    {selectedReport === "flightsConversations" && Array.isFullArray(flightsListReport) && <FlightsConvReports setExcelRows={setExcelRows} setExcelColumns={setExcelColumns} excelRows={excelRows} excelColumns={excelColumns} />}

                    {/* flight report */}
                    {selectedReport == "flightReport" && <FlightReport />}

                    {/* movement report (Dublin) — visual report, downloadable as PDF */}
                    {selectedReport === "movementReport" && <MovementReportPage />}

                </Col>
            </Row>



        </>
    )

    return (
        <NewScreen>
            <Row>
                <Col>
                    <Title subtitle={"Total reports"} description={"Preview all the reports in the system"} />
                </Col>
            </Row>
            {content}
        </NewScreen>
    );
}

export default TotalReports